import React, { useRef, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, ArrowRight } from "lucide-react";
import { SMS_TEXT, MARKETING_TEXT, getUtms } from "./ContactForm";
import { useSpamGuard } from "./useSpamGuard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const METHODS = ["Call", "Text", "Email"];
const empty = { fullName: "", phone: "", email: "", zip: "", concerns: "", contactMethod: "Call", smsConsent: false, marketingConsent: false };

const validate = (f) => {
  const e = {};
  if (f.fullName.trim().length < 2) e.fullName = "Please enter your name";
  if (f.phone.replace(/\D/g, "").length < 10) e.phone = "Enter a 10-digit phone number";
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email)) e.email = "Enter a valid email";
  if (!/^\d{5}$/.test(f.zip.trim())) e.zip = "Enter a 5-digit ZIP";
  return e;
};

export default function LeadForm({ dark = false, compact = false, source = "Website Form", id }) {
  const [form, setForm] = useState(empty);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const lock = useRef(false);
  const guard = useSpamGuard();
  const t = (s) => `${id}-${s}`;

  const set = (k, v) => { setForm((f) => ({ ...f, [k]: v })); setErrors((e) => ({ ...e, [k]: "" })); };
  const inp = `hw-input ${dark ? "hw-input-dark" : ""} ${compact ? "!py-2.5" : ""}`;
  const muted = dark ? "text-[#F4F1E9]/60" : "text-[#5F676B]";
  const label = dark ? "text-[#F4F1E9]/80" : "text-[#344C59]";

  const submit = async (e) => {
    e.preventDefault();
    if (lock.current) return;
    const er = validate(form);
    setErrors(er);
    if (Object.keys(er).length) return;
    lock.current = true;
    setSubmitting(true);
    const [firstName, ...rest] = form.fullName.trim().split(/\s+/);
    try {
      const { data } = await axios.post(`${API}/leads`, {
        firstName, lastName: rest.join(" "), phone: form.phone, email: form.email, zip: form.zip.trim(),
        concerns: form.concerns, contactMethod: form.contactMethod, message: form.concerns,
        smsConsent: form.smsConsent, marketingConsent: form.marketingConsent, termsAgreed: true,
        source, ...getUtms(), ...guard.fields(),
      });
      if (data.success) { setForm(empty); guard.reset(); setSent(true); }
    } catch (err) {
      toast.error(err.response?.status === 429 ? "Too many requests. Please call us or try again later." : "Something went wrong. Please call us or try again.");
    } finally {
      lock.current = false;
      setSubmitting(false);
    }
  };

  if (sent) {
    return (
      <div data-testid={t("success")} className="py-6">
        <p className={`overline-hw ${dark ? "text-[#8CAFC0]" : "text-[#344C59]"}`}>Request received</p>
        <h3 className={`font-heading text-3xl mt-3 ${dark ? "text-[#F4F1E9]" : "text-[#172B38]"}`}>Thank you. We'll be in touch shortly.</h3>
        <p className={`mt-3 text-[0.95rem] leading-relaxed ${muted}`}>A member of our local team will reach out by your preferred contact method to schedule your water test.</p>
        <button type="button" onClick={() => setSent(false)} data-testid={t("reset")} className={`mt-6 text-sm underline underline-offset-4 ${muted}`}>Send another request</button>
      </div>
    );
  }

  const Err = ({ k }) => errors[k] ? <p className="text-[#d97757] text-xs mt-1.5" data-testid={t(`error-${k}`)}>{errors[k]}</p> : null;

  return (
    <form data-testid={id} onSubmit={submit} noValidate className={`relative ${compact ? "space-y-3" : "space-y-4"}`}>
      {guard.Honeypot}
      <div>
        <label htmlFor={t("name")} className={`hw-label ${label} ${compact ? "sr-only" : ""}`}>Full name</label>
        <input id={t("name")} data-testid={t("name")} className={inp} autoComplete="name" placeholder={compact ? "Full name" : undefined} value={form.fullName} onChange={(e) => set("fullName", e.target.value)} />
        <Err k="fullName" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label htmlFor={t("phone")} className={`hw-label ${label} ${compact ? "sr-only" : ""}`}>Phone</label>
          <input id={t("phone")} data-testid={t("phone")} type="tel" autoComplete="tel" placeholder={compact ? "Phone" : undefined} className={inp} value={form.phone} onChange={(e) => set("phone", e.target.value)} />
          <Err k="phone" />
        </div>
        <div>
          <label htmlFor={t("zip")} className={`hw-label ${label} ${compact ? "sr-only" : ""}`}>ZIP code</label>
          <input id={t("zip")} data-testid={t("zip")} inputMode="numeric" maxLength={5} autoComplete="postal-code" placeholder={compact ? "ZIP code" : undefined} className={inp} value={form.zip} onChange={(e) => set("zip", e.target.value.replace(/\D/g, ""))} />
          <Err k="zip" />
        </div>
      </div>
      <div>
        <label htmlFor={t("email")} className={`hw-label ${label} ${compact ? "sr-only" : ""}`}>Email</label>
        <input id={t("email")} data-testid={t("email")} type="email" autoComplete="email" placeholder={compact ? "Email" : undefined} className={inp} value={form.email} onChange={(e) => set("email", e.target.value)} />
        <Err k="email" />
      </div>
      {!compact && (
        <div>
          <label htmlFor={t("concerns")} className={`hw-label ${label}`}>What water concerns are you experiencing? <span className="opacity-60">(optional)</span></label>
          <textarea id={t("concerns")} data-testid={t("concerns")} rows={3} className={`${inp} resize-none`} placeholder="Hard water spots, iron staining, taste, well water..." value={form.concerns} onChange={(e) => set("concerns", e.target.value)} />
        </div>
      )}
      <fieldset>
        <legend className={`hw-label ${label}`}>{compact ? "Best way to reach you" : "Preferred contact method"}</legend>
        <div className="grid grid-cols-3 border" style={{ borderColor: dark ? "rgba(244,241,233,0.28)" : "#D9D6CC" }}>
          {METHODS.map((m, i) => {
            const on = form.contactMethod === m;
            return (
              <button type="button" key={m} data-testid={t(`method-${m.toLowerCase()}`)} aria-pressed={on} onClick={() => set("contactMethod", m)}
                className={`${compact ? "py-2" : "py-2.5"} text-sm transition-colors ${i ? "border-l" : ""} ${on ? (dark ? "bg-[#F4F1E9] text-[#172B38]" : "bg-[#172B38] text-[#F4F1E9]") : (dark ? "text-[#F4F1E9]/80 hover:bg-white/5" : "bg-white text-[#344C59] hover:bg-[#F4F1E9]")}`}
                style={{ borderColor: dark ? "rgba(244,241,233,0.28)" : "#D9D6CC" }}>{m}</button>
            );
          })}
        </div>
      </fieldset>
      <div className="space-y-2.5 pt-1">
        {[["smsConsent", SMS_TEXT, "sms"], ["marketingConsent", MARKETING_TEXT, "marketing"]].map(([k, txt, s]) => (
          <label key={k} className="flex items-start gap-2.5 cursor-pointer">
            <input type="checkbox" data-testid={t(`${s}-consent`)} className="mt-0.5 w-4 h-4 shrink-0 accent-[#344C59]" checked={form[k]} onChange={(e) => set(k, e.target.checked)} />
            <span className={`${compact ? "text-[10px]" : "text-[11px]"} leading-snug ${muted}`}>{txt}</span>
          </label>
        ))}
      </div>
      <button type="submit" data-testid={t("submit")} className={`${dark ? "btn-light" : "btn-cta"} w-full ${compact ? "!py-3" : ""}`} disabled={submitting}>
        {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending…</> : <>Schedule a Water Test <ArrowRight className="w-4 h-4" strokeWidth={1.5} /></>}
      </button>
      <p className={`text-[11px] ${muted}`}>
        By submitting, you agree to our <Link to="/privacy" className="underline underline-offset-2">Privacy Policy</Link> and <Link to="/terms" className="underline underline-offset-2">Terms</Link>.
      </p>
    </form>
  );
}
